/**
 * WaitingRoom - Versicherten waiting room before video assessment
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { Label, Sheet, SheetContent, SheetHeader, SheetTitle, saveDevicePreference, getPreferredDeviceId } from "@jourfx/ui";
import {
  Loader2,
  LogOut,
  MessageSquare,
  Mic,
  MicOff,
  Send,
  Settings,
  User,
  Video,
  VideoOff,
} from "lucide-react";
import { useBrandLogoUrl } from "@/branding/BrandingProvider";

import { useSession } from "../../hooks/useSession";

type Id<T extends string> = string & { __tableName: T };

interface WaitingRoomProps {
  appointmentId: string;
  onAdmitted: () => void;
  onLeave: () => void;
  debug?: boolean;
}

export function WaitingRoom({ appointmentId, onAdmitted, onLeave, debug }: WaitingRoomProps) {
  const { t, i18n } = useTranslation("video");

  const primaryLogoUrl = useBrandLogoUrl("primary");
  const apiRef = api as any;

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  const [devices, setDevices] = useState<{
    cameras: MediaDeviceInfo[];
    microphones: MediaDeviceInfo[];
    speakers: MediaDeviceInfo[];
  }>({ cameras: [], microphones: [], speakers: [] });

  const [selectedCamera, setSelectedCamera] = useState("");
  const [selectedMic, setSelectedMic] = useState("");
  const [selectedSpeaker, setSelectedSpeaker] = useState("");

  const [deviceStatus, setDeviceStatus] = useState({
    camera: false,
    microphone: false,
    connection: true,
  });

  const { session } = useSession(appointmentId);
  const joinWaitingRoom = useMutation(api.video.admission.joinWaitingRoom);
  const leaveWaitingRoom = useMutation(api.video.admission.leaveWaitingRoom);
  const sendChatMessage = useMutation(apiRef.waitingRoomChat.index.sendAsVersicherter);
  const appointment = useQuery(
    api.appointments.index.getForVersicherter,
    session?.sessionToken
      ? {
          appointmentId,
          sessionToken: session.sessionToken,
        }
      : "skip"
  );

  const admissionStatus = useQuery(
    api.video.admission.getAdmissionStatus,
    session?.sessionToken
      ? {
          appointmentId: appointmentId as Id<"appointments">,
          sessionToken: session.sessionToken,
        }
      : "skip"
  );

  const waitingRoomMessages = useQuery(
    apiRef.waitingRoomChat.index.listForVersicherter,
    session?.sessionToken
      ? {
          appointmentId: appointmentId as Id<"appointments">,
          sessionToken: session.sessionToken,
        }
      : "skip"
  ) as Array<{
    _id: string;
    senderType: "versicherter" | "staff";
    senderName: string;
    message: string;
    createdAt: number;
  }> | undefined;

  // Join waiting room once session is available, then update device status when it changes
  useEffect(() => {
    if (!session?.sessionToken) return;

    const payload = {
      appointmentId: appointmentId as Id<"appointments">,
      sessionToken: session.sessionToken,
      deviceStatus: {
        cameraReady: deviceStatus.camera,
        microphoneReady: deviceStatus.microphone,
        connectionStable: deviceStatus.connection,
      },
      ...(debug ? { debug: true } : {}),
    };

    joinWaitingRoom(payload).catch((err) => {
      console.error("Failed to join waiting room:", err);
    });
  }, [appointmentId, debug, deviceStatus.camera, deviceStatus.connection, deviceStatus.microphone, joinWaitingRoom, session?.sessionToken]);

  useEffect(() => {
    if (admissionStatus?.admitted) {
      onAdmitted();
    }
  }, [admissionStatus?.admitted, onAdmitted]);

  useEffect(() => {
    async function initMedia() {
      try {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
        }

        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: selectedCamera ? { deviceId: selectedCamera } : true,
          audio: selectedMic ? { deviceId: selectedMic } : true,
        });

        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }

        streamRef.current = mediaStream;

        setDeviceStatus((prev) => ({
          ...prev,
          camera: mediaStream.getVideoTracks().length > 0,
          microphone: mediaStream.getAudioTracks().length > 0,
        }));

        const deviceList = await navigator.mediaDevices.enumerateDevices();
        const cams = deviceList.filter((device) => device.kind === "videoinput");
        const mics = deviceList.filter((device) => device.kind === "audioinput");
        const spks = deviceList.filter((device) => device.kind === "audiooutput");
        setDevices({ cameras: cams, microphones: mics, speakers: spks });

        // Restore preferred devices on first enumeration
        if (!selectedCamera && cams.length > 0) {
          const preferred = getPreferredDeviceId("camera", cams.map((d) => d.deviceId));
          if (preferred) setSelectedCamera(preferred);
        }
        if (!selectedMic && mics.length > 0) {
          const preferred = getPreferredDeviceId("microphone", mics.map((d) => d.deviceId));
          if (preferred) setSelectedMic(preferred);
        }
        if (!selectedSpeaker && spks.length > 0) {
          const preferred = getPreferredDeviceId("speaker", spks.map((d) => d.deviceId));
          if (preferred) setSelectedSpeaker(preferred);
        }
      } catch (error) {
        console.error("Failed to get media:", error);
        setDeviceStatus((prev) => ({
          ...prev,
          camera: false,
          microphone: false,
        }));
      }
    }

    initMedia();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [selectedCamera, selectedMic]);

  const toggleMic = () => {
    if (!streamRef.current) return;
    streamRef.current.getAudioTracks().forEach((track) => {
      track.enabled = !isMicOn;
    });
    setIsMicOn(!isMicOn);
  };

  const toggleCamera = () => {
    if (!streamRef.current) return;
    streamRef.current.getVideoTracks().forEach((track) => {
      track.enabled = !isCameraOn;
    });
    setIsCameraOn(!isCameraOn);
  };

  const handleLeaveConfirm = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (session?.sessionToken) {
      leaveWaitingRoom({
        appointmentId: appointmentId as Id<"appointments">,
        sessionToken: session.sessionToken,
      }).catch(console.error);
    }

    onLeave();
  };

  const handleSendMessage = async () => {
    const message = chatMessage.trim();
    if (!message || !session?.sessionToken) return;

    setIsSendingMessage(true);
    try {
      await sendChatMessage({
        appointmentId: appointmentId as Id<"appointments">,
        sessionToken: session.sessionToken,
        message,
      });
      setChatMessage("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    } catch (error) {
      console.error("Failed to send waiting room message", error);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const formatTimeString = (timestamp: number) => {
    const timezone = appointment?.timezone || "Europe/Berlin";
    return new Intl.DateTimeFormat(i18n.language, {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: timezone,
    }).format(new Date(timestamp));
  };

  const sortedMessages = useMemo(
    () => [...(waitingRoomMessages || [])].sort((a, b) => a.createdAt - b.createdAt),
    [waitingRoomMessages]
  );

  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [sortedMessages]);

  return (
    <div className="min-h-screen client-gradient-bg flex flex-col">
      <header className="p-4 sm:p-6">
        <img src={primaryLogoUrl} alt="JourFx" className="h-8 sm:h-10 lg:h-12 w-auto" />
      </header>

      <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-2xl lg:max-w-5xl">
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-4">
          <div className="client-card p-6 sm:p-8 flex-1 min-w-0">
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--sage-100)] rounded-full">
                <Loader2 className="w-4 h-4 text-[var(--sage-600)] animate-spin" />
                <span className="text-sm font-semibold text-[var(--sage-700)]">{t("waitingRoom.title")}</span>
              </div>
            </div>

            <div className="text-center mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--stone-800)] mb-2">
                {t("waitingRoom.pleaseWait")}
              </h1>
              <p className="text-lg text-[var(--stone-600)]">{t("waitingRoom.statusMessages.examinerWillAdmit")}</p>
            </div>

            <div className="bg-[var(--sage-50)] border border-[var(--sage-200)] rounded-xl p-4 mb-6">
              <p className="text-center text-[var(--sage-700)] font-medium">
                {t("waitingRoom.stayOnPage")}
                <br />
                {t("waitingRoom.autoConnect")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1.618fr_1fr] gap-6 mb-6">
              <div className="order-2 sm:order-1 space-y-4">
                <div className="relative rounded-xl overflow-hidden bg-[var(--stone-200)] border border-[var(--stone-200)]">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`block w-full aspect-[4/3] object-cover transition-opacity duration-300 ${!isCameraOn ? "opacity-0" : "opacity-100"}`}
                  />
                  <div className={`absolute inset-0 flex flex-col items-center justify-center bg-[var(--stone-200)] transition-opacity duration-300 ${isCameraOn ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
                    <div className="w-16 h-16 rounded-full bg-[var(--stone-300)] flex items-center justify-center mb-2">
                      <User className="w-8 h-8 text-[var(--stone-500)]" />
                    </div>
                    <span className="text-sm text-[var(--stone-500)] font-medium">{t("waitingRoom.cameraOff")}</span>
                  </div>

                  {/* "Your camera" label overlay */}
                  <div className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-md bg-black/40 backdrop-blur-sm">
                    <span className="text-xs font-medium text-white">{t("waitingRoom.yourCamera")}</span>
                  </div>

                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    <button
                      className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors shadow-lg ${
                        isMicOn
                          ? "bg-white text-[var(--stone-700)] hover:bg-[var(--stone-100)]"
                          : "bg-red-500 text-white hover:bg-red-600"
                      }`}
                      onClick={toggleMic}
                      aria-label={isMicOn ? t("controls.mute") : t("controls.unmute")}
                    >
                      {isMicOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
                    </button>
                    <button
                      className={`h-10 w-10 rounded-full flex items-center justify-center transition-colors shadow-lg ${
                        isCameraOn
                          ? "bg-white text-[var(--stone-700)] hover:bg-[var(--stone-100)]"
                          : "bg-red-500 text-white hover:bg-red-600"
                      }`}
                      onClick={toggleCamera}
                      aria-label={isCameraOn ? t("controls.videoOff") : t("controls.videoOn")}
                    >
                      {isCameraOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
                    </button>
                    <button
                      className="h-10 w-10 rounded-full flex items-center justify-center bg-white text-[var(--stone-700)] hover:bg-[var(--stone-100)] transition-colors shadow-lg"
                      onClick={() => setShowSettings(true)}
                      aria-label={t("controls.settings")}
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Device status badges */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    deviceStatus.camera && isCameraOn
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full transition-colors ${deviceStatus.camera && isCameraOn ? "bg-emerald-500" : "bg-red-500"}`} />
                    {!deviceStatus.camera ? t("waitingRoom.cameraNotAvailable") : isCameraOn ? t("waitingRoom.cameraReady") : t("waitingRoom.cameraOff")}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    deviceStatus.microphone && isMicOn
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full transition-colors ${deviceStatus.microphone && isMicOn ? "bg-emerald-500" : "bg-red-500"}`} />
                    {!deviceStatus.microphone ? t("waitingRoom.microphoneNotAvailable") : isMicOn ? t("waitingRoom.microphoneReady") : t("controls.mute")}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    deviceStatus.connection
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full transition-colors ${deviceStatus.connection ? "bg-emerald-500" : "bg-red-500"}`} />
                    {deviceStatus.connection ? t("waitingRoom.connectionStable") : t("waitingRoom.connectionUnstable")}
                  </span>
                </div>
              </div>

              <div className="order-1 sm:order-2 space-y-4">
                <div className="p-4 bg-white rounded-xl border border-[var(--stone-200)] shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[var(--sage-100)] flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {appointment?.gutachterAvatarUrl ? (
                        <img src={appointment.gutachterAvatarUrl} alt={appointment.gutachterName ?? ""} className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-6 h-6 text-[var(--sage-600)]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[var(--stone-800)] leading-tight">
                        {appointment?.gutachterName ?? t("waitingRoom.examiner")}
                      </p>
                      <p className="text-sm text-[var(--stone-500)]">{t("waitingRoom.yourExaminer")}</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-xl border border-[var(--stone-200)] shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[var(--stone-500)]">{t("waitingRoom.scheduledTime")}</span>
                    <span className="font-semibold text-[var(--stone-800)]">
                      {appointment?.scheduledAt ? formatTimeString(appointment.scheduledAt) : "-"}
                    </span>
                  </div>
                  {appointment?.duration && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[var(--stone-500)]">{t("waitingRoom.duration")}</span>
                      <span className="text-sm font-medium text-[var(--stone-700)]">{appointment.duration} min</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <button
              className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-[var(--stone-600)] bg-white border border-[var(--stone-200)] hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
              onClick={() => setShowLeaveConfirm(true)}
            >
              <LogOut className="w-5 h-5" />
              {t("waitingRoom.leaveAppointment")}
            </button>
          </div>

          {/* Chat panel - right column on desktop, below on mobile */}
          <div className="lg:w-80 xl:w-96 flex-shrink-0 flex">
            <div className="client-card p-4 sm:p-6 flex flex-col w-full min-h-[320px] lg:min-h-0">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare className="w-4 h-4 text-[var(--sage-600)]" />
                <p className="text-sm font-semibold text-[var(--stone-700)]">{t("chat.title")}</p>
              </div>
              <p className="text-xs text-[var(--stone-500)] mb-3">
                {t("chat.hint")}
              </p>

              {/* Messages area - fills remaining space */}
              <div className="chat-scroll flex-1 overflow-y-auto overscroll-contain rounded-xl bg-[var(--stone-50)] p-3 space-y-2">
                {sortedMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <MessageSquare className="w-6 h-6 text-[var(--stone-300)] mb-1.5" />
                    <p className="text-xs text-[var(--stone-400)]">{t("chat.noMessages")}</p>
                  </div>
                ) : (
                  sortedMessages.map((message) => {
                    const isVersicherter = message.senderType === "versicherter";
                    return (
                      <div
                        key={message._id}
                        className={`flex ${isVersicherter ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                            isVersicherter
                              ? "bg-[var(--sage-600)] text-white rounded-br-md"
                              : "bg-white border border-[var(--stone-200)] text-[var(--stone-800)] rounded-bl-md"
                          }`}
                        >
                          {!isVersicherter && (
                            <p className="text-xs font-semibold text-[var(--sage-700)] mb-0.5">
                              {message.senderName}
                            </p>
                          )}
                          <p className="text-sm break-words leading-relaxed whitespace-pre-wrap">{message.message}</p>
                          <p
                            className={`text-[10px] mt-0.5 text-right ${
                              isVersicherter ? "text-white/60" : "text-[var(--stone-400)]"
                            }`}
                          >
                            {formatTimeString(message.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input area - pinned to bottom */}
              <form
                className="flex items-end gap-2 mt-3 flex-shrink-0"
                onSubmit={(event) => {
                  event.preventDefault();
                  void handleSendMessage();
                }}
              >
                <textarea
                  ref={textareaRef}
                  value={chatMessage}
                  onChange={(event) => {
                    setChatMessage(event.target.value);
                    resizeTextarea();
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      void handleSendMessage();
                    }
                  }}
                  placeholder={t("chat.placeholder")}
                  rows={1}
                  className="chat-scroll flex-1 resize-none rounded-xl border border-[var(--stone-200)] bg-white px-3 py-2 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-[var(--sage-300)] focus:border-[var(--sage-300)] max-h-24 overflow-y-auto [field-sizing:content]"
                />
                <button
                  type="submit"
                  disabled={!chatMessage.trim() || isSendingMessage}
                  className="rounded-xl bg-[var(--sage-600)] p-2.5 text-white hover:bg-[var(--sage-700)] disabled:opacity-40 disabled:hover:bg-[var(--sage-600)] transition-colors flex-shrink-0"
                  aria-label={t("chat.send")}
                >
                  {isSendingMessage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </form>
            </div>
          </div>
          </div>
        </div>
      </main>

      <Sheet open={showSettings} onOpenChange={setShowSettings} modal={false}>
        <SheetContent hideOverlay className="bg-white">
          <SheetHeader>
            <SheetTitle>{t("waitingRoom.settings.title")}</SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            <div>
              <Label htmlFor="camera-select">{t("waitingRoom.settings.camera")}</Label>
              <select
                id="camera-select"
                value={selectedCamera}
                onChange={(event) => { setSelectedCamera(event.target.value); saveDevicePreference("camera", event.target.value); }}
                className="mt-1 w-full p-3 border border-[var(--stone-200)] rounded-xl bg-white text-[var(--stone-800)]"
                aria-label={t("waitingRoom.settings.selectCamera")}
              >
                {devices.cameras.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || t("waitingRoom.settings.camera")}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="mic-select">{t("waitingRoom.settings.microphone")}</Label>
              <select
                id="mic-select"
                value={selectedMic}
                onChange={(event) => { setSelectedMic(event.target.value); saveDevicePreference("microphone", event.target.value); }}
                className="mt-1 w-full p-3 border border-[var(--stone-200)] rounded-xl bg-white text-[var(--stone-800)]"
                aria-label={t("waitingRoom.settings.selectMicrophone")}
              >
                {devices.microphones.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || t("waitingRoom.settings.microphone")}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="speaker-select">{t("waitingRoom.settings.speaker")}</Label>
              <select
                id="speaker-select"
                value={selectedSpeaker}
                onChange={(event) => { setSelectedSpeaker(event.target.value); saveDevicePreference("speaker", event.target.value); }}
                className="mt-1 w-full p-3 border border-[var(--stone-200)] rounded-xl bg-white text-[var(--stone-800)]"
                aria-label={t("waitingRoom.settings.selectSpeaker")}
              >
                {devices.speakers.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || t("waitingRoom.settings.speaker")}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {showLeaveConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <button
            type="button"
            aria-label={t("waitingRoom.leaveConfirm.backToWaiting")}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowLeaveConfirm(false)}
          />

          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                <LogOut className="w-7 h-7 text-red-600" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-[var(--stone-800)] text-center mb-2">
              {t("waitingRoom.leaveConfirm.title")}
            </h2>

            <p className="text-[var(--stone-600)] text-center mb-4">{t("waitingRoom.leaveConfirm.message")}</p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-amber-800 font-medium mb-2">{t("waitingRoom.leaveConfirm.note")}</p>
              <ul className="text-sm text-amber-700 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">-</span>
                  <span>{t("waitingRoom.leaveConfirm.examinerNotified")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5">-</span>
                  <span>{t("waitingRoom.leaveConfirm.rescheduleInfo")}</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="flex-1 px-6 py-3 rounded-xl font-semibold text-[var(--stone-700)] bg-[var(--stone-100)] hover:bg-[var(--stone-200)] transition-colors"
              >
                {t("waitingRoom.leaveConfirm.backToWaiting")}
              </button>
              <button
                onClick={handleLeaveConfirm}
                className="flex-1 px-6 py-3 rounded-xl font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                {t("waitingRoom.leaveConfirm.confirmLeave")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
